// src/pages/ProfilePage.js

import React, { useState, useEffect } from 'react'; // Added useEffect for potential future use
import {
    Card,
    Avatar,
    Typography,
    Descriptions,
    Button,
    Space,
    Modal,
    Form,
    Input,
    message, // Ant Design message component
    Spin,    // Loading indicator
    Row,
    Col,
    Tag      // For displaying role
} from 'antd';
import {
    UserOutlined,                // For avatar and input prefix
    MailOutlined,                // For Descriptions item
    SafetyCertificateOutlined,   // For Descriptions item (Role)
    CalendarOutlined,            // For Descriptions item (Date)
    EditOutlined,                // For Edit button
    LockOutlined                 // For Password button and input prefix
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext'; // Adjust path as necessary
import { updateUser } from '../services/mockUserService'; // Adjust path as necessary

// Destructure Typography components for cleaner usage
const { Title, Text } = Typography; // Added Paragraph just in case

// --- Edit Profile Form Component ---
// Defined within ProfilePage scope for simplicity and direct access to props/state if needed later
const EditProfileForm = ({ initialValues, onFinish, onCancel, loading }) => {
    const [form] = Form.useForm();

    // Reset form fields when initialValues change (e.g., modal reopens)
    // This ensures the form shows the latest data if the user context was updated elsewhere
    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue(initialValues);
        }
    }, [form, initialValues]);

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            // initialValues={initialValues} // Setting via useEffect is often more reliable with modals
            name="edit_profile_form" // Added form name for potential debugging
        >
            <Form.Item
                name="name"
                label="Full Name"
                rules={[{ required: true, message: 'Please input your full name!' }]}
            >
                <Input prefix={<UserOutlined />} placeholder="Enter your full name" />
            </Form.Item>
            {/* Add other editable fields here if necessary, e.g., phone */}
            {/* <Form.Item name="phone" label="Phone Number">
                <Input prefix={<PhoneOutlined />} placeholder="Optional phone number" />
            </Form.Item> */}
            <Form.Item style={{ textAlign: 'right', marginBottom: 0, marginTop: 24 }}>
                <Space>
                    <Button onClick={onCancel} disabled={loading}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Save Changes
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
};

// --- Change Password Form Component ---
// Defined within ProfilePage scope
const ChangePasswordForm = ({ onFinish, onCancel, loading }) => {
    const [form] = Form.useForm();

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            name="change_password_form"
        >
            {/* NOTE: In a real application, you MUST include a 'currentPassword' field
                 and verify it on the backend before allowing a password change. */}
            {/* Example (commented out for mock):
            <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[{ required: true, message: 'Please enter your current password!' }]}
            >
                <Input.Password prefix={<LockOutlined />} placeholder="Your current password" />
            </Form.Item>
            */}
            <Form.Item
                name="password" // Field for the NEW password
                label="New Password"
                rules={[
                    { required: true, message: 'Please enter your new password!' },
                    { min: 6, message: 'Password must be at least 6 characters long.' } // Example validation
                ]}
                hasFeedback // Adds success/error icons based on validation status
            >
                <Input.Password prefix={<LockOutlined />} placeholder="Enter new password (min. 6 chars)" />
            </Form.Item>
            <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['password']} // This field depends on the 'password' field
                hasFeedback
                rules={[
                    { required: true, message: 'Please confirm your new password!' },
                    // Custom validator function
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                                // If passwords match or field is empty (covered by required rule)
                                return Promise.resolve();
                            }
                            // If passwords don't match
                            return Promise.reject(new Error('The two passwords that you entered do not match!'));
                        },
                    }),
                ]}
            >
                <Input.Password prefix={<LockOutlined />} placeholder="Confirm your new password" />
            </Form.Item>
            <Form.Item style={{ textAlign: 'right', marginBottom: 0, marginTop: 24 }}>
                <Space>
                    <Button onClick={onCancel} disabled={loading}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Update Password
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
};


// --- Main Profile Page Component ---
const ProfilePage = () => {
    const { user, setUser } = useAuth(); // Get current user and setter from Auth context
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
    const [loading, setLoading] = useState(false); // Handles loading state for API calls

    // Conditional rendering: Show loading spinner if user data is not yet available
    if (!user) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 200px)' }}>
                <Spin size="large" tip="Loading Profile..." />
            </div>
        );
    }

    // --- Modal Control Functions ---
    const showEditModal = () => setIsEditModalVisible(true);
    const handleEditCancel = () => setIsEditModalVisible(false);
    const showPasswordModal = () => setIsPasswordModalVisible(true);
    const handlePasswordCancel = () => setIsPasswordModalVisible(false);

    // --- Form Submission Logic ---
    const handleEditFinish = async (values) => {
        setLoading(true);
        console.log('Submitting profile update:', values);
        try {
            // Prepare only the data that needs updating
            const dataToUpdate = {
                name: values.name,
                // Add other fields if they become editable e.g., phone: values.phone
            };
            // Call the mock service to update the user
            const updatedUserDataFromService = await updateUser(user.id, dataToUpdate);

            // Update the user state in the AuthContext
            // Merge existing user data with potentially partial data returned from service
            setUser(currentUser => ({ ...currentUser, ...updatedUserDataFromService }));

            message.success('Profile updated successfully!');
            setIsEditModalVisible(false); // Close modal on success
        } catch (error) {
            message.error(`Failed to update profile: ${error.message || 'Please try again.'}`);
            console.error("Profile update error:", error);
        } finally {
            setLoading(false); // Ensure loading is turned off
        }
    };

    const handlePasswordFinish = async (values) => {
        setLoading(true);
        console.log("Submitting password change request..."); // Avoid logging password value
        try {
             // Call the mock service - ensure it can handle just the password
            await updateUser(user.id, { password: values.password });
            // Don't update the context with the password hash

            message.success('Password updated successfully!');
            setIsPasswordModalVisible(false); // Close modal on success
        } catch (error) {
            message.error(`Failed to update password: ${error.message || 'Please try again.'}`);
            console.error("Password update error:", error);
        } finally {
            setLoading(false); // Ensure loading is turned off
        }
    };

    // --- Helper Function for Role Tag ---
    const getRoleTag = (role) => {
        let color = 'geekblue'; // Default color
        let icon = <SafetyCertificateOutlined />; // Default icon
        let formattedRole = 'User'; // Default text

        if (typeof role === 'string') {
            formattedRole = role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()); // Capitalize words
            switch (role.toLowerCase()) {
                case 'administrator':
                    color = 'volcano';
                    break;
                case 'manager':
                    color = 'blue';
                    break;
                case 'viewer':
                    color = 'cyan';
                    break;
                default:
                    break; // Keep default color/icon
            }
        }
        return <Tag color={color} icon={icon} style={{ margin: 0 }}>{formattedRole}</Tag>;
    };

    // --- Date Formatting ---
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        } catch (e) {
            return 'Invalid Date';
        }
    };

    // --- Render JSX ---
    return (
        <Row justify="center" style={{ padding: '0 16px' /* Add padding on smaller screens */ }}>
            <Col xs={24} sm={24} md={20} lg={16} xl={14}> {/* Responsive column width */}
                <Card
                    bordered={false}
                    style={{
                        width: '100%',
                        borderRadius: '8px',
                        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)', // Slightly more pronounced shadow
                    }}
                    // Action buttons moved to header for prominence
                    title={
                        <Title level={4} style={{ margin: 0, color: 'rgba(0, 0, 0, 0.85)' }}>
                           <UserOutlined style={{ marginRight: 10 }} /> My Profile
                        </Title>
                    }
                    extra={
                        <Space wrap> {/* Allow buttons to wrap on small screens */}
                            <Button icon={<EditOutlined />} onClick={showEditModal} disabled={loading}>
                                Edit Profile
                            </Button>
                            <Button icon={<LockOutlined />} onClick={showPasswordModal} disabled={loading}>
                                Change Password
                            </Button>
                        </Space>
                    }
                >
                    {/* Profile header section */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #f0f0f0' }}>
                         <Avatar
                             size={128}
                             style={{
                                 backgroundColor: '#87d068', // Use a consistent color or derive from user ID/name
                                 marginBottom: '16px',
                                 border: '4px solid #ffffff',
                                 boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                             }}
                             icon={<UserOutlined />}
                             // src={user.avatarUrl} // Uncomment if you have avatar URLs
                         />
                        <Title level={3} style={{ marginBottom: '4px' }}>{user.name}</Title>
                         <Text type="secondary" style={{ fontSize: '1.05em' }}>{user.email}</Text>
                    </div>

                    {/* Details using Descriptions component */}
                    <Descriptions
                         // title="Account Details" // Optional title within Descriptions
                         bordered
                         column={1} // Force single column for consistency
                         size="default" // "middle" or "small" are also options
                         layout="horizontal"
                         labelStyle={{ width: '180px', background: '#fafafa', fontWeight: 500 }} // Style labels
                     >
                        <Descriptions.Item label={<><UserOutlined style={{marginRight: 8}} /> Full Name</>}>
                            {user.name || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<><MailOutlined style={{marginRight: 8}} /> Email Address</>}>
                            {user.email || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<><SafetyCertificateOutlined style={{marginRight: 8}} /> User Role</>}>
                            {getRoleTag(user.role)}
                        </Descriptions.Item>
                        <Descriptions.Item label={<><CalendarOutlined style={{marginRight: 8}} /> Member Since</>}>
                            {formatDate(user.createdAt)}
                        </Descriptions.Item>
                        {/* Add other relevant details from the user object if available */}
                        {/* Example:
                        <Descriptions.Item label={<><PhoneOutlined style={{marginRight: 8}} /> Phone</>}>
                            {user.phone || 'Not Provided'}
                        </Descriptions.Item>
                         */}
                    </Descriptions>
                </Card>
            </Col>

            {/* --- Modals --- */}

            {/* Edit Profile Modal */}
            <Modal
                title={<><EditOutlined style={{ marginRight: 8 }}/> Edit Your Profile</>}
                open={isEditModalVisible}
                onCancel={handleEditCancel} // Use cancel handler
                footer={null} // Footer is rendered within the Form component
                destroyOnClose={true} // Crucial: Unmounts form when closed, resetting state
                maskClosable={false} // Prevent accidental close by clicking outside
                width={550} // Slightly wider if needed
            >
                {/* Render the form only when the modal is open to ensure it gets fresh initialValues */}
                 {isEditModalVisible && (
                    <EditProfileForm
                        initialValues={{
                            name: user.name,
                            // phone: user.phone, // Pass other initial values if editable
                         }}
                        onFinish={handleEditFinish}
                        onCancel={handleEditCancel}
                        loading={loading}
                    />
                )}
            </Modal>

            {/* Change Password Modal */}
             <Modal
                title={<><LockOutlined style={{ marginRight: 8 }}/> Change Your Password</>}
                open={isPasswordModalVisible}
                onCancel={handlePasswordCancel} // Use cancel handler
                footer={null} // Footer is rendered within the Form component
                destroyOnClose={true} // Crucial: Unmounts form when closed
                maskClosable={false}
                width={550}
            >
                {/* Render the form only when the modal is open */}
                {isPasswordModalVisible && (
                    <ChangePasswordForm
                        onFinish={handlePasswordFinish}
                        onCancel={handlePasswordCancel}
                        loading={loading}
                    />
                )}
            </Modal>
        </Row>
    );
};

export default ProfilePage;